import _ from 'lodash'
import { produce, castDraft, Draft, immerable } from 'immer'

import Reference from 'bibliography/domain/Reference'
import { Text } from 'transliteration/domain/text'
import { Museum } from 'fragmentarium/domain/museum'
import Folio from 'fragmentarium/domain/Folio'
import { Genres } from 'fragmentarium/domain/Genres'
import { Joins } from 'fragmentarium/domain/join'
import { Acquisition } from 'fragmentarium/domain/Acquisition'
import { Session } from 'auth/Session'
import {
  ExternalNumber,
  ExternalNumbers,
  ExternalNumberTypes,
} from 'fragmentarium/domain/FragmentDtos'
import { RecordEntry } from 'fragmentarium/domain/RecordEntry'
import { ResearchProject } from 'research-projects/researchProject'
import { MesopotamianDate } from 'chronology/domain/Date'
import { Archaeology } from 'fragmentarium/domain/archaeology'
import { Colophon } from 'fragmentarium/domain/Colophon'
import { DossierReference } from 'dossiers/domain/DossierReference'
import {
  NamedEntity,
  RealiaInfoEntry,
  RealiaNamedEntity,
} from 'fragmentarium/ui/text-annotation/EntityType'
import {
  Introduction,
  Measures,
  Notes,
  Script,
  UncuratedReference,
} from 'fragmentarium/domain/fragmentTypes'

export * from 'fragmentarium/domain/fragmentTypes'
interface FragmentProps {
  number: string
  accession: string
  publication: string
  acquisitions: ReadonlyArray<Acquisition>
  description: string
  joins: Joins
  measures: Measures
  collection: string
  legacyScript: string
  cdliImages: readonly string[]
  folios: ReadonlyArray<Folio>
  record: ReadonlyArray<RecordEntry>
  text: Text
  notes: Notes
  museum: Museum
  references: ReadonlyArray<Reference>
  uncuratedReferences?: ReadonlyArray<UncuratedReference> | null
  traditionalReferences: readonly string[]
  atf: string
  hasPhoto: boolean
  genres: Genres
  introduction: Introduction
  script: Script
  externalNumbers: ExternalNumbers
  projects: ReadonlyArray<ResearchProject>
  date?: MesopotamianDate
  datesInText?: ReadonlyArray<MesopotamianDate>
  archaeology?: Archaeology
  colophon?: Colophon
  authorizedScopes?: string[]
  dossiers: ReadonlyArray<DossierReference>
  namedEntities?: ReadonlyArray<NamedEntity>
  realia?: ReadonlyArray<RealiaNamedEntity>
  realiaInfo?: ReadonlyArray<RealiaInfoEntry>
}

export class Fragment {
  readonly [immerable] = true

  readonly number: string
  readonly accession: string
  readonly publication: string
  readonly acquisitions: ReadonlyArray<Acquisition>
  readonly description: string
  readonly joins: Joins
  readonly measures: Measures
  readonly collection: string
  readonly legacyScript: string
  readonly cdliImages: readonly string[]
  readonly folios: ReadonlyArray<Folio>
  readonly record: ReadonlyArray<RecordEntry>
  readonly text: Text
  readonly notes: Notes
  readonly museum: Museum
  readonly references: ReadonlyArray<Reference>
  readonly uncuratedReferences: ReadonlyArray<UncuratedReference> | null
  readonly traditionalReferences: readonly string[]
  readonly atf: string
  readonly hasPhoto: boolean
  readonly genres: Genres
  readonly introduction: Introduction
  readonly script: Script
  readonly externalNumbers: ExternalNumbers
  readonly projects: ReadonlyArray<ResearchProject>
  readonly dossiers: ReadonlyArray<DossierReference>
  readonly date?: MesopotamianDate
  readonly datesInText?: ReadonlyArray<MesopotamianDate>
  readonly archaeology?: Archaeology
  readonly colophon?: Colophon
  readonly authorizedScopes?: string[]
  readonly namedEntities?: ReadonlyArray<NamedEntity>
  readonly realia?: ReadonlyArray<RealiaNamedEntity>
  readonly realiaInfo?: ReadonlyArray<RealiaInfoEntry>

  constructor(props: FragmentProps) {
    this.number = props.number
    this.accession = props.accession
    this.publication = props.publication
    this.acquisitions = props.acquisitions ?? []
    this.description = props.description
    this.joins = props.joins
    this.measures = props.measures
    this.collection = props.collection
    this.legacyScript = props.legacyScript
    this.cdliImages = props.cdliImages
    this.folios = props.folios
    this.record = props.record
    this.text = props.text
    this.notes = props.notes
    this.museum = props.museum
    this.references = props.references
    this.uncuratedReferences = props.uncuratedReferences ?? null
    this.traditionalReferences = props.traditionalReferences
    this.atf = props.atf
    this.hasPhoto = props.hasPhoto
    this.genres = props.genres
    this.introduction = props.introduction
    this.script = props.script
    this.externalNumbers = props.externalNumbers
    this.projects = props.projects
    this.dossiers = props.dossiers
    this.date = props.date
    this.datesInText = props.datesInText
    this.archaeology = props.archaeology
    this.colophon = props.colophon
    this.authorizedScopes = props.authorizedScopes
    this.namedEntities = props.namedEntities
    this.realia = props.realia
    this.realiaInfo = props.realiaInfo
  }

  static create(props: FragmentProps): Fragment {
    return new Fragment(props)
  }

  get hasUncuratedReferences(): boolean {
    return !_.isNil(this.uncuratedReferences)
  }

  get uniqueRecord(): ReadonlyArray<RecordEntry> {
    const reducer = (
      filteredRecord: RecordEntry[],
      recordEntry: RecordEntry,
    ): RecordEntry[] => {
      const last = _.last(filteredRecord)
      const keepRecord = !last || !last.dateEquals(recordEntry)
      if (keepRecord) {
        filteredRecord.push(recordEntry)
      }
      return filteredRecord
    }
    return this.record.reduce(reducer, [])
  }

  setReferences(references: ReadonlyArray<Reference>): Fragment {
    return produce(this, (draft: Draft<Fragment>) => {
      draft.references = castDraft(references)
    })
  }

  filterFolios(session: Session): Fragment {
    return produce(this, (draft: Draft<Fragment>) => {
      draft.folios = this.folios.filter((folio) =>
        session.isAllowedToReadFolio(folio),
      )
    })
  }

  public getExternalNumber(numberType: ExternalNumber): string {
    return this.externalNumbers[numberType] || ''
  }
  get oraccNumbers(): readonly string[] {
    return this.externalNumbers['oraccNumbers'] || []
  }
  get sealNumbers(): readonly string[] {
    return this.externalNumbers['sealNumbers'] || []
  }
  get hasExternalResources(): boolean {
    return _.some([
      ...this.oraccNumbers,
      ...this.sealNumbers,
      ...ExternalNumberTypes.map((number) =>
        this.getExternalNumber(number as ExternalNumber),
      ),
    ])
  }

  get atfHeading(): string {
    const cdliNumber = this.getExternalNumber('cdliNumber') || 'X000001'
    return `&${cdliNumber} = ${this.number}
#project: eblo
#atf: lang akk-x-stdbab
#atf: use unicode
#atf: use math
#atf: use legacy`
  }
}
